import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon
} from '@mui/icons-material';
import { supabase } from '../utils/supabase';
import { useTheme } from '../contexts/ThemeContext';

export default function SettingsPage({ session }) {
  const { isDarkMode, toggleTheme } = useTheme();
  
  // User profile state
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  
  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Notification preferences state
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    notification_days: 3
  });
  
  // UI state
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchUserPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async () => {
    try {
      if (!session?.user) {
        setError('Please log in to view settings');
        return;
      }
      
      const user = session.user;
      setEmail(user.email || '');
      setDisplayName(user.user_metadata?.display_name || user.user_metadata?.username || '');
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user data');
    }
  };

  const fetchUserPreferences = async () => {
    try {
      
      if (!session?.user) {
        throw new Error('User not authenticated');
      }

      const userId = session.user.id;

      // Use direct REST API call to avoid hanging
      const response = await Promise.race([
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/user_preferences?user_id=eq.${userId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/vnd.pgrst.object+json'
          }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]);

      if (!response.ok) {
        if (response.status === 406) {
          // No preferences found - use defaults
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data) {
        setPreferences({
          email_notifications: data.email_notifications,
          notification_days: data.notification_days
        });
      }
    } catch (error) {
      console.error('⚙️ [SettingsPage] Error fetching user preferences:', error);
      // Don't show error for missing preferences, use defaults
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setProfileLoading(true);
      setError(null);
      setSuccess('');

      const { error } = await supabase.auth.updateUser({
        data: {
          display_name: displayName,
          username: displayName
        }
      });

      if (error) throw error;

      setSuccess('Profile updated successfully!');
      await fetchUserData(); // Refresh user data
      
      // Dispatch custom event to notify other components (like Navbar) of profile update
      window.dispatchEvent(new CustomEvent('userProfileUpdated', {
        detail: { 
          displayName: displayName,
          timestamp: Date.now()
        }
      }));
      
      // Note: Removed supabase.auth.refreshSession() as it corrupts session state
      // The profile update should be reflected immediately
    } catch (error) {
      console.error('⚙️ [SettingsPage] Error updating profile:', error);
      setError('Failed to update profile: ' + error.message);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    try {
      setPasswordLoading(true);
      setError(null);
      setSuccess('');

      // Validate passwords
      if (newPassword !== confirmPassword) {
        setError('New passwords do not match');
        return;
      }

      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      setSuccess('Password updated successfully!');
      setNewPassword('');
      setConfirmPassword('');
      setConfirmDialog(false);
    } catch (error) {
      console.error('Error updating password:', error);
      setError('Failed to update password: ' + error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpdateNotifications = async () => {
    try {
      setNotificationLoading(true);
      setError(null);
      setSuccess('');
            
      if (!session?.user) {
        throw new Error('User not authenticated');
      }

      const userId = session.user.id;
      const preferenceData = {
        user_id: userId,
        email_notifications: preferences.email_notifications,
        notification_days: preferences.notification_days,
        updated_at: new Date().toISOString()
      };

      // Check if preferences exist using direct REST API
      const checkResponse = await Promise.race([
        fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/user_preferences?user_id=eq.${userId}&select=id`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json'
          }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Check request timeout')), 5000)
        )
      ]);

      let existingData = null;
      if (checkResponse.ok) {
        const checkResult = await checkResponse.json();
        existingData = checkResult.length > 0 ? checkResult[0] : null;
      }

      let response;
      if (existingData) {
        // Update existing preferences
        response = await Promise.race([
          fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/user_preferences?user_id=eq.${userId}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify(preferenceData)
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Update request timeout')), 10000)
          )
        ]);
      } else {
        // Insert new preferences
        response = await Promise.race([
          fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/user_preferences`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify(preferenceData)
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Insert request timeout')), 10000)
          )
        ]);
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      setSuccess('Notification preferences updated successfully!');
    } catch (error) {
      console.error('⚙️ [SettingsPage] Error saving preferences:', error);
      setError('Failed to save notification preferences: ' + error.message);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleToggleEmailNotifications = (event) => {
    setPreferences(prev => ({
      ...prev,
      email_notifications: event.target.checked
    }));
  };

  const handleNotificationDaysChange = (event) => {
    setPreferences(prev => ({
      ...prev,
      notification_days: event.target.value
    }));
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4">
            Settings
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Profile Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6">Profile Settings</Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Display Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      disabled={profileLoading}
                      helperText="This name will be displayed throughout the app"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      value={email}
                      disabled
                      helperText="Email cannot be changed from this interface"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleUpdateProfile}
                      disabled={profileLoading}
                      startIcon={<SaveIcon />}
                    >
                      {profileLoading ? 'Updating...' : 'Update Profile'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Security Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <SecurityIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6">Security Settings</Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type={showNewPassword ? 'text' : 'password'}
                      label="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={passwordLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText="Minimum 6 characters"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      label="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={passwordLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={confirmPassword !== '' && newPassword !== confirmPassword}
                      helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={() => setConfirmDialog(true)}
                      disabled={passwordLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                      startIcon={<SaveIcon />}
                    >
                      {passwordLoading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Notification Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6">Notification Settings</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Configure how you want to be notified about expiring ingredients.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.email_notifications}
                        onChange={handleToggleEmailNotifications}
                        disabled={notificationLoading}
                      />
                    }
                    label="Enable Email Notifications"
                  />

                  <Typography variant="body2" color="text.secondary" sx={{ mt: -2, ml: 4 }}>
                    Receive email alerts when your ingredients are about to expire.
                  </Typography>

                  {preferences.email_notifications && (
                    <>
                      <FormControl sx={{ minWidth: 300 }}>
                        <InputLabel>Notify me when items expire in...</InputLabel>
                        <Select
                          value={preferences.notification_days}
                          onChange={handleNotificationDaysChange}
                          label="Notify me when items expire in..."
                          disabled={notificationLoading}
                        >
                          <MenuItem value={1}>1 day</MenuItem>
                          <MenuItem value={2}>2 days</MenuItem>
                          <MenuItem value={3}>3 days</MenuItem>
                          <MenuItem value={5}>5 days</MenuItem>
                          <MenuItem value={7}>1 week</MenuItem>
                          <MenuItem value={14}>2 weeks</MenuItem>
                        </Select>
                      </FormControl>

                      <Typography variant="body2" color="text.secondary">
                        You'll receive notifications for items expiring within the selected timeframe.
                      </Typography>
                    </>
                  )}

                  <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 1, color: 'info.contrastText' }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Email notifications are sent daily at 9 AM UTC. 
                      Make sure your email address is verified to receive notifications.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={handleUpdateNotifications}
                    disabled={notificationLoading}
                    startIcon={<SaveIcon />}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {notificationLoading ? 'Saving...' : 'Save Notification Preferences'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Theme Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  {isDarkMode ? (
                    <DarkModeIcon sx={{ mr: 2, color: 'primary.main' }} />
                  ) : (
                    <LightModeIcon sx={{ mr: 2, color: 'primary.main' }} />
                  )}
                  <Typography variant="h6">Theme Settings</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Customize the appearance of the Virtual Kitchen app.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={isDarkMode}
                        onChange={toggleTheme}
                        icon={<LightModeIcon />}
                        checkedIcon={<DarkModeIcon />}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>Dark Mode</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({isDarkMode ? 'On' : 'Off'})
                        </Typography>
                      </Box>
                    }
                  />

                  <Typography variant="body2" color="text.secondary" sx={{ mt: -2, ml: 4 }}>
                    {isDarkMode 
                      ? 'Dark mode reduces eye strain in low-light environments' 
                      : 'Light mode provides better visibility in bright environments'
                    }
                  </Typography>

                  <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 1, color: 'info.contrastText' }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Your theme preference is automatically saved and will persist across all devices where you're logged in.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Password Confirmation Dialog */}
        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Password Change</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Are you sure you want to change your password?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You will remain logged in on this device, but you'll need to use your new password to log in elsewhere.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)} disabled={passwordLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePassword} 
              variant="contained" 
              disabled={passwordLoading}
            >
              {passwordLoading ? 'Updating...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
